import { byLocale } from '@makan/command-center/kit';
import type { FeedbackKind, FeedbackStatus, ToolkitMode } from '../domain/values.ts';

/** Every word the toolkit and the inbox show, one object per locale of the same type. */
export interface FeedbackStrings {
  readonly kinds: Readonly<Record<FeedbackKind, string>>;
  readonly statuses: Readonly<Record<FeedbackStatus, string>>;
  readonly pill: {
    readonly open: string;
    readonly close: string;
    readonly snip: string;
    readonly record: string;
    readonly voice: string;
    readonly mine: string;
    readonly moveHint: string;
    readonly shortcut: string;
  };
  readonly snip: { readonly hint: string; readonly hintTouch: string; readonly cancel: string; readonly capturing: string; readonly failed: string };
  readonly annotate: {
    readonly tools: string;
    readonly pen: string;
    readonly highlighter: string;
    readonly box: string;
    readonly ellipse: string;
    readonly arrow: string;
    readonly text: string;
    readonly colours: string;
    readonly colourNames: Readonly<Record<'red' | 'amber' | 'green' | 'blue' | 'ink' | 'white', string>>;
    readonly undo: string;
    readonly redo: string;
    readonly typeHere: string;
    readonly canvas: string;
  };
  readonly record: {
    readonly countdown: string;
    readonly recording: string;
    readonly paused: string;
    readonly pause: string;
    readonly resume: string;
    readonly stop: string;
    readonly micOff: string;
    readonly denied: string;
    readonly voiceDenied: string;
    readonly limit: (minutes: number) => string;
    readonly voiceNote: string;
  };
  readonly compose: {
    readonly title: string;
    readonly close: string;
    readonly kind: string;
    readonly description: string;
    readonly descriptionHint: string;
    readonly descriptionNeeded: string;
    readonly send: string;
    readonly sending: string;
    readonly sent: string;
    readonly sentLead: string;
    readonly failed: string;
    readonly tooLarge: string;
    readonly off: string;
    readonly discard: string;
    readonly preview: string;
    readonly context: string;
  };
  readonly mine: { readonly title: string; readonly empty: string; readonly loadFailed: string; readonly reply: string; readonly media: (count: number) => string };
  readonly inbox: {
    readonly title: string;
    readonly lead: string;
    readonly empty: string;
    readonly loadFailed: string;
    readonly allStatuses: string;
    readonly status: string;
    readonly item: string;
    readonly sent: string;
    readonly from: string;
    readonly media: string;
    readonly slots: Readonly<Record<'screenshot' | 'recording' | 'voice', string>>;
    readonly touch: string;
    readonly open: (description: string) => string;
    readonly none: string;
    readonly description: string;
    readonly where: string;
    readonly path: string;
    readonly version: string;
    readonly browser: string;
    readonly viewport: string;
    readonly language: string;
    readonly market: string;
    readonly clock: string;
    readonly steps: string;
    readonly noSteps: string;
    readonly problems: string;
    readonly noProblems: string;
    readonly moveTo: string;
    readonly replies: string;
    readonly noReplies: string;
    readonly reply: string;
    readonly replySend: string;
    readonly github: string;
    readonly githubSend: string;
    readonly githubAsked: string;
    readonly githubOpened: (number: number) => string;
    readonly githubNotOpened: (reason: string) => string;
    readonly githubAgain: string;
    readonly githubReasons: Readonly<Record<'not configured' | 'tracker refused' | 'tracker down' | 'timed out' | 'no such feedback', string>>;
    readonly roles: Readonly<Record<'manager' | 'cashier', string>>;
    readonly settings: string;
    readonly settingsLead: string;
    readonly mode: string;
    readonly modes: Readonly<Record<ToolkitMode, string>>;
    readonly testers: string;
    readonly testersLead: string;
    readonly noTesters: string;
    readonly addMe: string;
    readonly you: string;
    readonly addById: string;
    readonly staffId: string;
    readonly add: string;
    readonly remove: string;
    readonly saved: string;
    readonly failed: string;
  };
}

const en: FeedbackStrings = {
  kinds: { bug: 'Bug', idea: 'Idea', blocker: 'Blocker' },
  statuses: { new: 'New', seen: 'Seen', 'in progress': 'In progress', 'sent to github': 'Sent to GitHub', fixed: 'Fixed', "won't do": "Won't do" },
  pill: {
    open: 'Feedback',
    close: 'Close feedback',
    snip: 'Snip',
    record: 'Record',
    voice: 'Voice note',
    mine: 'My feedback',
    moveHint: 'Drag to move it to any edge.',
    shortcut: 'Cmd+Shift+F',
  },
  snip: {
    hint: 'Drag over the part to send, or click for the whole screen. Esc cancels.',
    hintTouch: 'Drag over the part to send, or tap for the whole screen.',
    cancel: 'Cancel',
    capturing: 'Taking the picture...',
    failed: 'Could not take the picture. Try again.',
  },
  annotate: {
    tools: 'Markup tools',
    pen: 'Pen',
    highlighter: 'Highlighter',
    box: 'Box',
    ellipse: 'Circle',
    arrow: 'Arrow',
    text: 'Text',
    colours: 'Colour',
    colourNames: { red: 'Red', amber: 'Amber', green: 'Green', blue: 'Blue', ink: 'Black', white: 'White' },
    undo: 'Undo',
    redo: 'Redo',
    typeHere: 'Type, then Enter',
    canvas: 'The picture, to draw on',
  },
  record: {
    countdown: 'Recording starts in',
    recording: 'Recording',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop',
    micOff: 'No microphone: recording the screen only.',
    denied: 'Screen recording was not allowed.',
    voiceDenied: 'The microphone was not allowed.',
    limit: (minutes) => `Up to ${String(minutes)} minutes`,
    voiceNote: 'Voice note',
  },
  compose: {
    title: 'Send feedback',
    close: 'Close',
    kind: 'What is it?',
    description: 'What happened, or what should?',
    descriptionHint: 'What you did, what you expected, what you saw.',
    descriptionNeeded: 'Say what happened in a few words.',
    send: 'Send',
    sending: 'Sending...',
    sent: 'Thanks, it is in.',
    sentLead: 'Find it under My feedback with its status and replies.',
    failed: 'It did not send. Your words are still here; try again.',
    tooLarge: 'That recording is too large to send. Record a shorter one.',
    off: 'The feedback toolkit is turned off for this group.',
    discard: 'Discard',
    preview: 'Recording',
    context: 'Sent with it: this screen, your browser and the last steps and errors. Nothing you typed into the page.',
  },
  mine: {
    title: 'My feedback',
    empty: 'Nothing sent yet. Snip or record anything that looks wrong.',
    loadFailed: 'Could not load your feedback.',
    reply: 'Reply',
    media: (count) => (count === 1 ? '1 attachment' : `${String(count)} attachments`),
  },
  inbox: {
    title: 'Feedback',
    lead: 'What staff and testers sent from the screens they were on.',
    empty: 'No feedback yet.',
    loadFailed: 'Could not load the feedback.',
    allStatuses: 'Every status',
    status: 'Status',
    item: 'Item',
    sent: 'Sent',
    from: 'From',
    media: 'Media',
    slots: { screenshot: 'Screenshot', recording: 'Recording', voice: 'Voice note' },
    touch: 'touch',
    open: (description) => `Open: ${description}`,
    none: 'None',
    description: 'What they said',
    where: 'Where',
    path: 'Screen',
    version: 'App version',
    browser: 'Browser',
    viewport: 'Window',
    language: 'Language',
    market: 'Market',
    clock: 'Their clock',
    steps: 'Last steps',
    noSteps: 'No steps recorded.',
    problems: 'Errors and failed requests',
    noProblems: 'None seen.',
    moveTo: 'Move to',
    replies: 'Replies',
    noReplies: 'No reply yet.',
    reply: 'Reply to the sender',
    replySend: 'Send reply',
    github: 'GitHub',
    githubSend: 'Send to GitHub',
    githubAsked: 'Opening the issue...',
    githubOpened: (number) => `Issue #${String(number)}`,
    githubNotOpened: (reason) => `No issue opened: ${reason}.`,
    githubAgain: 'Try again',
    githubReasons: { 'not configured': 'GitHub is not set up on this cloud', 'tracker refused': 'GitHub refused it', 'tracker down': 'GitHub did not answer', 'timed out': 'GitHub took too long', 'no such feedback': 'the item is gone' },
    roles: { manager: 'Manager', cashier: 'Cashier' },
    settings: 'Toolkit settings',
    settingsLead: 'Who sees the feedback pill.',
    mode: 'Who sees it',
    modes: { environment: 'All staff, outside production', testers: 'All staff outside production, and testers in production', off: 'Nobody' },
    testers: 'Testers',
    testersLead: 'Testers see the toolkit in production when it is on for testers.',
    noTesters: 'No testers yet.',
    addMe: 'Add me',
    you: 'you',
    addById: 'Add a tester by staff id',
    staffId: 'Staff id',
    add: 'Add',
    remove: 'Remove',
    saved: 'Saved.',
    failed: 'That did not save. Try again.',
  },
};

const zh: FeedbackStrings = {
  kinds: { bug: '问题', idea: '建议', blocker: '阻碍' },
  statuses: { new: '新', seen: '已看', 'in progress': '处理中', 'sent to github': '已送 GitHub', fixed: '已修复', "won't do": '不处理' },
  pill: {
    open: '反馈',
    close: '关闭反馈',
    snip: '截图',
    record: '录屏',
    voice: '语音',
    mine: '我的反馈',
    moveHint: '拖动可移到任意边缘。',
    shortcut: 'Cmd+Shift+F',
  },
  snip: {
    hint: '拖选要发送的部分，或点击截取整个屏幕。按 Esc 取消。',
    hintTouch: '拖选要发送的部分，或轻点截取整个屏幕。',
    cancel: '取消',
    capturing: '正在截图...',
    failed: '截图失败，请再试一次。',
  },
  annotate: {
    tools: '标注工具',
    pen: '画笔',
    highlighter: '荧光笔',
    box: '方框',
    ellipse: '圆圈',
    arrow: '箭头',
    text: '文字',
    colours: '颜色',
    colourNames: { red: '红', amber: '黄', green: '绿', blue: '蓝', ink: '黑', white: '白' },
    undo: '撤销',
    redo: '重做',
    typeHere: '输入后按 Enter',
    canvas: '可标注的截图',
  },
  record: {
    countdown: '录制即将开始',
    recording: '录制中',
    paused: '已暂停',
    pause: '暂停',
    resume: '继续',
    stop: '停止',
    micOff: '没有麦克风：只录屏幕。',
    denied: '未允许录屏。',
    voiceDenied: '未允许使用麦克风。',
    limit: (minutes) => `最长 ${String(minutes)} 分钟`,
    voiceNote: '语音',
  },
  compose: {
    title: '发送反馈',
    close: '关闭',
    kind: '这是什么？',
    description: '发生了什么，或应该怎样？',
    descriptionHint: '你做了什么、预期什么、看到什么。',
    descriptionNeeded: '请用几句话说明发生了什么。',
    send: '发送',
    sending: '发送中...',
    sent: '谢谢，已收到。',
    sentLead: '在「我的反馈」查看状态和回复。',
    failed: '发送失败。你写的内容还在，请再试一次。',
    tooLarge: '录像太大，无法发送。请录短一点。',
    off: '此集团已关闭反馈工具。',
    discard: '放弃',
    preview: '录像',
    context: '一并发送：此画面、你的浏览器、最近的步骤和错误。不含你在页面中输入的内容。',
  },
  mine: {
    title: '我的反馈',
    empty: '还没有发送。看到不对的地方，就截图或录屏。',
    loadFailed: '无法载入你的反馈。',
    reply: '回复',
    media: (count) => `${String(count)} 个附件`,
  },
  inbox: {
    title: '反馈',
    lead: '员工和测试人员在所在画面发送的反馈。',
    empty: '还没有反馈。',
    loadFailed: '无法载入反馈。',
    allStatuses: '全部状态',
    status: '状态',
    item: '内容',
    sent: '发送时间',
    from: '发送人',
    media: '附件',
    slots: { screenshot: '截图', recording: '录屏', voice: '语音' },
    touch: '触屏',
    open: (description) => `打开：${description}`,
    none: '无',
    description: '反馈内容',
    where: '位置',
    path: '画面',
    version: '应用版本',
    browser: '浏览器',
    viewport: '窗口',
    language: '语言',
    market: '市场',
    clock: '对方时间',
    steps: '最近步骤',
    noSteps: '没有记录步骤。',
    problems: '错误和失败的请求',
    noProblems: '没有。',
    moveTo: '改为',
    replies: '回复',
    noReplies: '还没有回复。',
    reply: '回复发送人',
    replySend: '发送回复',
    github: 'GitHub',
    githubSend: '送到 GitHub',
    githubAsked: '正在建立 issue...',
    githubOpened: (number) => `Issue #${String(number)}`,
    githubNotOpened: (reason) => `未建立 issue：${reason}。`,
    githubAgain: '再试一次',
    githubReasons: { 'not configured': '此云端未设置 GitHub', 'tracker refused': 'GitHub 拒绝了', 'tracker down': 'GitHub 没有回应', 'timed out': 'GitHub 太久没回应', 'no such feedback': '此反馈已不存在' },
    roles: { manager: '经理', cashier: '收银员' },
    settings: '工具设置',
    settingsLead: '谁能看到反馈按钮。',
    mode: '谁能看到',
    modes: { environment: '正式环境以外的全部员工', testers: '正式环境以外的全部员工，以及正式环境中的测试人员', off: '没有人' },
    testers: '测试人员',
    testersLead: '工具对测试人员开启时，测试人员在正式环境也能看到。',
    noTesters: '还没有测试人员。',
    addMe: '加入我',
    you: '你',
    addById: '按员工 ID 加入测试人员',
    staffId: '员工 ID',
    add: '加入',
    remove: '移除',
    saved: '已保存。',
    failed: '保存失败，请再试一次。',
  },
};

const ms: FeedbackStrings = {
  kinds: { bug: 'Masalah', idea: 'Cadangan', blocker: 'Halangan' },
  statuses: { new: 'Baharu', seen: 'Dilihat', 'in progress': 'Sedang diuruskan', 'sent to github': 'Dihantar ke GitHub', fixed: 'Dibaiki', "won't do": 'Tidak akan dibuat' },
  pill: {
    open: 'Maklum balas',
    close: 'Tutup maklum balas',
    snip: 'Tangkap skrin',
    record: 'Rakam',
    voice: 'Nota suara',
    mine: 'Maklum balas saya',
    moveHint: 'Seret untuk alihkan ke mana-mana tepi.',
    shortcut: 'Cmd+Shift+F',
  },
  snip: {
    hint: 'Seret di atas bahagian yang hendak dihantar, atau klik untuk seluruh skrin. Esc untuk batal.',
    hintTouch: 'Seret di atas bahagian yang hendak dihantar, atau ketik untuk seluruh skrin.',
    cancel: 'Batal',
    capturing: 'Mengambil gambar...',
    failed: 'Tidak dapat mengambil gambar. Cuba lagi.',
  },
  annotate: {
    tools: 'Alat penanda',
    pen: 'Pen',
    highlighter: 'Penyerlah',
    box: 'Kotak',
    ellipse: 'Bulatan',
    arrow: 'Anak panah',
    text: 'Teks',
    colours: 'Warna',
    colourNames: { red: 'Merah', amber: 'Kuning', green: 'Hijau', blue: 'Biru', ink: 'Hitam', white: 'Putih' },
    undo: 'Buat asal',
    redo: 'Buat semula',
    typeHere: 'Taip, kemudian tekan Enter',
    canvas: 'Gambar untuk dilukis',
  },
  record: {
    countdown: 'Rakaman bermula dalam',
    recording: 'Merakam',
    paused: 'Dijeda',
    pause: 'Jeda',
    resume: 'Sambung',
    stop: 'Henti',
    micOff: 'Tiada mikrofon: merakam skrin sahaja.',
    denied: 'Rakaman skrin tidak dibenarkan.',
    voiceDenied: 'Mikrofon tidak dibenarkan.',
    limit: (minutes) => `Sehingga ${String(minutes)} minit`,
    voiceNote: 'Nota suara',
  },
  compose: {
    title: 'Hantar maklum balas',
    close: 'Tutup',
    kind: 'Apakah ini?',
    description: 'Apa yang berlaku, atau apa yang sepatutnya berlaku?',
    descriptionHint: 'Apa yang anda buat, apa yang anda jangka, apa yang anda lihat.',
    descriptionNeeded: 'Terangkan apa yang berlaku dalam beberapa perkataan.',
    send: 'Hantar',
    sending: 'Menghantar...',
    sent: 'Terima kasih, sudah diterima.',
    sentLead: 'Lihat di bawah Maklum balas saya untuk status dan balasannya.',
    failed: 'Tidak berjaya dihantar. Tulisan anda masih ada; cuba lagi.',
    tooLarge: 'Rakaman itu terlalu besar untuk dihantar. Rakam yang lebih pendek.',
    off: 'Alat maklum balas dimatikan untuk kumpulan ini.',
    discard: 'Buang',
    preview: 'Rakaman',
    context: 'Dihantar bersama: skrin ini, pelayar anda serta langkah dan ralat terakhir. Tiada apa yang anda taip dalam halaman.',
  },
  mine: {
    title: 'Maklum balas saya',
    empty: 'Belum ada yang dihantar. Tangkap skrin atau rakam apa-apa yang nampak salah.',
    loadFailed: 'Tidak dapat memuatkan maklum balas anda.',
    reply: 'Balasan',
    media: (count) => `${String(count)} lampiran`,
  },
  inbox: {
    title: 'Maklum balas',
    lead: 'Apa yang dihantar oleh kakitangan dan penguji dari skrin yang mereka buka.',
    empty: 'Belum ada maklum balas.',
    loadFailed: 'Tidak dapat memuatkan maklum balas.',
    allStatuses: 'Semua status',
    status: 'Status',
    item: 'Perkara',
    sent: 'Dihantar',
    from: 'Daripada',
    media: 'Media',
    slots: { screenshot: 'Tangkapan skrin', recording: 'Rakaman', voice: 'Nota suara' },
    touch: 'sentuh',
    open: (description) => `Buka: ${description}`,
    none: 'Tiada',
    description: 'Apa yang mereka kata',
    where: 'Lokasi',
    path: 'Skrin',
    version: 'Versi aplikasi',
    browser: 'Pelayar',
    viewport: 'Tetingkap',
    language: 'Bahasa',
    market: 'Pasaran',
    clock: 'Waktu mereka',
    steps: 'Langkah terakhir',
    noSteps: 'Tiada langkah direkodkan.',
    problems: 'Ralat dan permintaan yang gagal',
    noProblems: 'Tiada yang dilihat.',
    moveTo: 'Alih ke',
    replies: 'Balasan',
    noReplies: 'Belum ada balasan.',
    reply: 'Balas kepada penghantar',
    replySend: 'Hantar balasan',
    github: 'GitHub',
    githubSend: 'Hantar ke GitHub',
    githubAsked: 'Membuka isu...',
    githubOpened: (number) => `Isu #${String(number)}`,
    githubNotOpened: (reason) => `Tiada isu dibuka: ${reason}.`,
    githubAgain: 'Cuba lagi',
    githubReasons: { 'not configured': 'GitHub belum disediakan pada cloud ini', 'tracker refused': 'GitHub menolaknya', 'tracker down': 'GitHub tidak menjawab', 'timed out': 'GitHub mengambil masa terlalu lama', 'no such feedback': 'perkara itu sudah tiada' },
    roles: { manager: 'Pengurus', cashier: 'Juruwang' },
    settings: 'Tetapan alat',
    settingsLead: 'Siapa yang nampak butang maklum balas.',
    mode: 'Siapa yang nampak',
    modes: { environment: 'Semua kakitangan, di luar persekitaran sebenar', testers: 'Semua kakitangan di luar persekitaran sebenar, dan penguji dalam persekitaran sebenar', off: 'Tiada sesiapa' },
    testers: 'Penguji',
    testersLead: 'Penguji nampak alat ini dalam persekitaran sebenar apabila ia dihidupkan untuk penguji.',
    noTesters: 'Belum ada penguji.',
    addMe: 'Tambah saya',
    you: 'anda',
    addById: 'Tambah penguji melalui ID kakitangan',
    staffId: 'ID kakitangan',
    add: 'Tambah',
    remove: 'Buang',
    saved: 'Disimpan.',
    failed: 'Tidak berjaya disimpan. Cuba lagi.',
  },
};

export const t: FeedbackStrings = byLocale({ en, zh, ms });
